import { useEffect, useState } from 'react';
import {
  BarChart3, ShoppingCart, Megaphone, MapPin, LogOut, Users2, MessageCircle, Settings,
  Database, ClipboardList, Store, Package, Tags, Link2, Truck, FileText, Handshake, Zap, ShieldCheck, ShoppingBasket,
} from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarHeader, SidebarFooter, useSidebar,
} from '@/components/ui/sidebar';
import sisthokoLogo from '@/assets/sisthoko-logo-white.png';
import { BetaBadge } from '@/components/BetaBadge';
import { useUserRoles } from '@/lib/roles';
import { UserPlus, Building2, CheckSquare } from 'lucide-react';

interface NavItem { title: string; url: string; icon: React.ElementType }
interface NavGroup { label: string; items: NavItem[] }

const foodpreneurGroups: NavGroup[] = [
  {
    label: 'Sourcing',
    items: [
      { title: 'Dashboard', url: '/dashboard', icon: BarChart3 },
      { title: 'My Basket', url: '/settings/basket', icon: ShoppingCart },
      { title: 'Deals & Promotions', url: '/dashboard', icon: Megaphone },
      { title: 'My Orders', url: '/orders', icon: Package },
    ],
  },
  {
    label: 'Account',
    items: [
      { title: 'WhatsApp Settings', url: '/settings/whatsapp', icon: MessageCircle },
      { title: 'My Location', url: '/settings/location', icon: MapPin },
      { title: 'Basket Preferences', url: '/settings/basket', icon: ShoppingBasket },
      { title: 'Profile', url: '/settings/profile', icon: Settings },
    ],
  },
];

const sellerGroup: NavGroup = {
  label: 'Seller Console',
  items: [
    { title: 'Overview', url: '/seller', icon: BarChart3 },
    { title: 'Orders', url: '/seller/orders', icon: ShoppingCart },
    { title: 'My Products', url: '/seller/products', icon: Package },
    { title: 'Storefront', url: '/seller/storefront', icon: Store },
  ],
};

const adminGroups: NavGroup[] = [
  {
    label: 'Sis Thoko',
    items: [
      { title: 'Dashboard', url: '/dashboard', icon: BarChart3 },
    ],
  },
  {
    label: 'Legacy Operations',
    items: [
      { title: 'Post Trade Interest', url: '/brand/trade', icon: FileText },
      { title: 'Deal Cards', url: '/brand/deals', icon: ShoppingCart },
      { title: 'Product Index', url: '/spaza/products', icon: ShoppingCart },
      { title: 'My Orders', url: '/spaza/orders', icon: Package },
      { title: 'Issue RFQ', url: '/spaza/rfq', icon: FileText },
      { title: 'Bid Dashboard', url: '/spaza/bids', icon: ShoppingCart },
      { title: 'Collaborative Bids', url: '/spaza/collab-bids', icon: Handshake },
      { title: 'Promotions', url: '/community/promotions', icon: Megaphone },
      { title: 'Demand Map', url: '/community/demand', icon: MapPin },
      { title: 'Delivery Management', url: '/logistics/deliveries', icon: Truck },
      { title: 'Market Intelligence', url: '/intelligence', icon: Zap },
    ],
  },
  {
    label: 'Data Management',
    items: [
      { title: 'Product Requests', url: '/data-capturer/requests', icon: ClipboardList },
      { title: 'Product Management', url: '/data-capturer/products', icon: Package },
      { title: 'Store Management', url: '/data-capturer/stores', icon: Store },
      { title: 'Category Management', url: '/data-capturer/categories', icon: Tags },
      { title: 'Scrape Management', url: '/data-capturer/scrapes', icon: Link2 },
    ],
  },
];

const dataCapturerGroups: NavGroup[] = [
  {
    label: 'Data Capturer',
    items: [
      { title: 'Dashboard', url: '/dashboard', icon: BarChart3 },
      { title: 'Product Requests', url: '/data-capturer/requests', icon: ClipboardList },
      { title: 'Store Management', url: '/data-capturer/stores', icon: Store },
      { title: 'Product Management', url: '/data-capturer/products', icon: Package },
      { title: 'Category Management', url: '/data-capturer/categories', icon: Tags },
      { title: 'Scrape Management', url: '/data-capturer/scrapes', icon: Link2 },
      { title: 'Market Intelligence', url: '/intelligence', icon: Zap },
    ],
  },
];

function groupsForRole(role: string): NavGroup[] {
  if (role === 'admin') return adminGroups;
  if (role === 'data_capturer') return dataCapturerGroups;
  return foodpreneurGroups;
}

function labelForRole(role: string): string {
  if (role === 'admin') return 'Admin';
  if (role === 'data_capturer') return 'Data Capturer';
  return 'Foodpreneur';
}

export function AppSidebar() {
  const { profile, session } = useAuth();
  const { state } = useSidebar();
  const navigate = useNavigate();
  const collapsed = state === 'collapsed';
  const [isSeller, setIsSeller] = useState(false);
  const { roles } = useUserRoles();

  useEffect(() => {
    if (!session?.user?.id) { setIsSeller(false); return; }
    supabase.from('seller_profiles').select('is_active').eq('user_id', session.user.id).maybeSingle()
      .then(({ data }) => setIsSeller(!!data?.is_active));
  }, [session?.user?.id]);

  const role = profile?.role || 'spaza';
  const baseGroups = groupsForRole(role);
  let groups: NavGroup[] = [...baseGroups];
  if (isSeller && role !== 'admin' && role !== 'data_capturer') {
    groups = [...groups, sellerGroup];
  }
  if (profile?.is_admin) {
    groups = [...groups, { label: 'Admin Tools', items: [
      { title: 'Order Management', url: '/admin/orders', icon: ShieldCheck },
      { title: 'Seller Applications', url: '/admin/sellers', icon: Handshake },
    ] }];
  }
  if (roles.includes('rep')) {
    groups = [{ label: 'Field Rep', items: [
      { title: 'Rep Dashboard', url: '/staff/rep', icon: BarChart3 },
      { title: 'Foodpreneur Approvals', url: '/admin/foodpreneurs/approvals', icon: Users2 },
      { title: 'Build Basket', url: '/spaza/products', icon: ShoppingBasket },
      { title: 'Product Requests', url: '/data-capturer/requests', icon: ClipboardList },
    ] }];
  }
  if (roles.includes('capturer')) {
    groups = [{ label: 'Data Capturer', items: [
      { title: 'Capturer Dashboard', url: '/staff/capturer', icon: BarChart3 },
      { title: 'Catalogue', url: '/admin/catalogue', icon: Database },
      { title: 'Products', url: '/admin/data/products', icon: Package },
      { title: 'Scrapes', url: '/admin/data/scrapes', icon: Link2 },
      { title: 'Stores', url: '/admin/data/stores', icon: Store },
      { title: 'Categories', url: '/admin/data/categories', icon: Tags },
      { title: 'Product Requests', url: '/admin/data/requests', icon: ClipboardList },
    ] }];
  }
  if (roles.includes('manager')) {
    groups = [
      { label: 'Manager', items: [
        { title: 'Manager Dashboard', url: '/staff/manager', icon: BarChart3 },
        { title: 'Approvals Hub', url: '/admin/approvals', icon: ShieldCheck },
        { title: 'Supplier Applications', url: '/admin/suppliers/applications', icon: Building2 },
        { title: 'Supplier Changes', url: '/staff/manager/supplier-changes', icon: CheckSquare },
        { title: 'Product Requests', url: '/admin/data/requests', icon: ClipboardList },
      ] },
      { label: 'Manage', items: [
        { title: 'Create User', url: '/staff/manager/users', icon: UserPlus },
        { title: 'Onboard Supplier', url: '/staff/manager/suppliers/new', icon: Building2 },
      ] },
    ];
  }
  if (roles.includes('supplier') && !roles.includes('system_admin') && !roles.includes('manager')) {
    groups = [{ label: 'Supplier', items: [
      { title: 'My Company', url: '/supplier', icon: Building2 },
      { title: 'Add Product', url: '/supplier/products/new', icon: Package },
      { title: 'Edit Profile', url: '/supplier/profile', icon: Settings },
    ] }];
  }

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-4">
        {!collapsed ? (
          <div className="flex items-center justify-center gap-2 mb-3">
            <img src={sisthokoLogo} alt="Sis Thoko" className="h-12 object-contain" style={{ width: 'auto' }} />
            <BetaBadge variant="inverted" />
          </div>
        ) : (
          <div className="mx-auto w-11 h-11 flex items-center justify-center mb-2">
            <img src={sisthokoLogo} alt="Sis Thoko" className="h-9 object-contain" style={{ width: 'auto' }} />
          </div>
        )}
        {!collapsed && (
          <div className="bg-sidebar-accent border border-sidebar-border rounded-md px-3 py-1.5 text-[11px] text-sidebar-accent-foreground">
            {labelForRole(role)} Portal
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        {groups.map((group) => (
          <SidebarGroup key={group.label}>
            {!collapsed && <SidebarGroupLabel className="text-sidebar-foreground/50 text-[10px] uppercase tracking-wider">{group.label}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.title + item.url}>
                    <SidebarMenuButton asChild>
                      <NavLink to={item.url} end className="hover:bg-sidebar-accent/50" activeClassName="bg-sidebar-accent text-sidebar-primary font-medium">
                        <item.icon className="mr-2 h-4 w-4" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="p-4">
        {!collapsed && profile && (
          <div className="text-xs text-sidebar-foreground/60 mb-2 px-2 truncate">
            {profile.full_name || profile.email}
          </div>
        )}
        {!collapsed && (
          <Button variant="ghost" onClick={handleLogout} className="w-full justify-start text-sidebar-foreground/60 hover:text-sidebar-accent-foreground hover:bg-sidebar-accent text-xs">
            <LogOut className="mr-2 h-4 w-4" /> Sign Out
          </Button>
        )}
        {!collapsed && (
          <div className="text-[10px] text-sidebar-foreground/40 text-center mt-3 leading-snug">
            Powered by © 2026 Imaginary Solutions Global (Pty) Ltd
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
